import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { intakeAPI, patientChartAPI } from '../../lib/api';
import { 
  Queue, 
  Clock,
  UserCircle,
  MagnifyingGlass,
  Funnel,
  ArrowClockwise,
  Stethoscope,
  CheckCircle,
  Warning
} from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function PatientQueuePage() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [viewMode, setViewMode] = useState('queue'); // 'queue' or 'all'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load queue first (always accessible to CMA)
      const queueRes = await intakeAPI.getQueue();
      setQueue(queueRes.data || []);
      
      // Try to load all patients (may fail if CMA doesn&apos;t have admin access)
      try {
        const patientsRes = await patientChartAPI.getPatientsList();
        const patients = patientsRes.data?.patients || [];
        setAllPatients(patients);
      } catch (patientError) {
        process.env.NODE_ENV === "development" && console.log('Could not load all patients (admin access required)');
        // Extract unique patients from queue as fallback
        const queuePatients = (queueRes.data || []).map(q => ({
          id: q.patient_id,
          first_name: q.patient_name?.split(' ')[0] || 'Patient',
          last_name: q.patient_name?.split(' ').slice(1).join(' ') || '',
          email: q.patient_email,
          created_at: q.created_at
        }));
        setAllPatients(queuePatients);
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error loading queue:', error);
      toast.error('Failed to load queue data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'in_triage': return 'bg-blue-100 text-blue-700';
      case 'ready_for_provider': return 'bg-green-100 text-green-700';
      case 'completed': return 'bg-slate-100 text-slate-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getPriorityIndicator = (intake) => {
    if (intake?.pain_level >= 8) return { color: 'bg-red-500', label: 'Urgent' };
    if (intake?.pain_level >= 5) return { color: 'bg-amber-500', label: 'Moderate' };
    return { color: 'bg-green-500', label: 'Routine' };
  };

  const getWaitTime = (createdAt) => {
    if (!createdAt) return 'N/A';
    const minutes = Math.round((Date.now() - new Date(createdAt).getTime()) / (1000 * 60));
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const handleStartTriage = (intakeId) => {
    navigate(`/cma/triage/${intakeId}`);
  };

  const handleSelectPatient = (patient) => {
    // Check if patient has an existing intake
    const existingIntake = queue.find(q => q.patient_id === patient.id);
    if (existingIntake) {
      navigate(`/cma/triage/${existingIntake.id}`);
    } else {
      // Navigate to patient chart or create new intake
      navigate(`/staff/patients/${patient.id}/chart`);
    }
  };

  // Filter queue based on search and status
  const filteredQueue = queue.filter(intake => {
    const matchesSearch = searchTerm === '' || 
      (intake.patient_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (intake.patient_email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (intake.chief_complaint?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || intake.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Filter patients based on search
  const filteredPatients = allPatients.filter(patient => {
    return searchTerm === '' || 
      (`${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (patient.email?.toLowerCase().includes(searchTerm.toLowerCase()));
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="patient-queue-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Patient Queue
        </h1>
        <p className="text-slate-500 mt-1">Select a patient to begin or continue triage</p>
      </div>

      {/* View Toggle & Search */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        {/* View Mode Toggle */}
        <div className="flex bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('queue')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'queue' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="view-queue-btn"
          >
            <Queue size={16} className="inline mr-2" />
            Active Queue ({queue.length})
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'all' ? 'bg-white text-teal-600 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="view-all-btn"
          >
            <UserCircle size={16} className="inline mr-2" />
            All Patients ({allPatients.length})
          </button>
        </div>

        {/* Search */}
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={viewMode === 'queue' ? "Search queue by name, email, or complaint..." : "Search patients by name or email..."}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-clinical pl-10 w-full"
            data-testid="search-input"
          />
        </div>

        {/* Status Filter (Queue view only) */}
        {viewMode === 'queue' && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-clinical w-48"
            data-testid="status-filter"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_triage">In Triage</option>
            <option value="ready_for_provider">Ready for Provider</option>
          </select>
        )}

        {/* Refresh */}
        <button
          onClick={loadData}
          className="btn-outline flex items-center gap-2"
          data-testid="refresh-btn"
        >
          <ArrowClockwise size={18} />
          Refresh
        </button>
      </div>

      {/* Queue View */}
      {viewMode === 'queue' && (
        <div className="card-clinical overflow-hidden">
          {filteredQueue.length === 0 ? (
            <div className="p-12 text-center">
              <Queue size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No patients in queue</p>
              <p className="text-slate-400 text-sm mt-2">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search or filters' 
                  : 'Patients will appear here after completing intake'}
              </p>
            </div>
          ) : (
            <table className="w-full" data-testid="queue-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Chief Complaint</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Priority</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Wait Time</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQueue.map((intake) => {
                  const priority = getPriorityIndicator(intake);
                  return (
                    <tr 
                      key={intake.id} 
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleStartTriage(intake.id)}
                      data-testid={`queue-row-${intake.id}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                            <UserCircle size={20} className="text-teal-600" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{intake.patient_name || 'Unknown Patient'}</div>
                            <div className="text-sm text-slate-500">{intake.patient_email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="max-w-xs">
                          <p className="text-slate-900 truncate">{intake.chief_complaint || 'No complaint recorded'}</p>
                          {intake.symptoms?.length > 0 && (
                            <p className="text-sm text-slate-500 truncate">
                              Symptoms: {intake.symptoms.slice(0, 3).join(', ')}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center gap-2">
                          <span className={`w-3 h-3 rounded-full ${priority.color}`} />
                          <span className="text-sm font-medium">{priority.label}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(intake.status)}`}>
                          {intake.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="flex items-center justify-center gap-1 text-slate-600">
                          <Clock size={14} />
                          {getWaitTime(intake.created_at)}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStartTriage(intake.id);
                          }}
                          className="btn-teal text-sm py-2 px-4"
                          data-testid={`triage-btn-${intake.id}`}
                        >
                          <Stethoscope size={16} className="inline mr-1" />
                          {intake.status === 'pending' ? 'Start Triage' : 'Continue'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* All Patients View */}
      {viewMode === 'all' && (
        <div className="card-clinical overflow-hidden">
          {filteredPatients.length === 0 ? (
            <div className="p-12 text-center">
              <UserCircle size={48} className="text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 text-lg">No patients found</p>
              <p className="text-slate-400 text-sm mt-2">
                {searchTerm ? 'Try adjusting your search' : 'No registered patients yet'}
              </p>
            </div>
          ) : (
            <table className="w-full" data-testid="patients-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Patient</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Email</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Queue Status</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Registered</th>
                  <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient) => {
                  const inQueue = queue.find(q => q.patient_id === patient.id);
                  return (
                    <tr 
                      key={patient.id} 
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleSelectPatient(patient)}
                      data-testid={`patient-row-${patient.id}`}
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                            <UserCircle size={20} className="text-slate-500" />
                          </div>
                          <div className="font-medium text-slate-900">
                            {patient.first_name} {patient.last_name}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-slate-600">{patient.email}</td>
                      <td className="py-4 px-4 text-center">
                        {inQueue ? (
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(inQueue.status)}`}>
                            {inQueue.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-sm">Not in queue</span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-center text-sm text-slate-500">
                        {new Date(patient.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4 text-center">
                        {inQueue ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartTriage(inQueue.id);
                            }}
                            className="btn-teal text-sm py-2 px-4"
                          >
                            <Stethoscope size={16} className="inline mr-1" />
                            {inQueue.status === 'pending' ? 'Start Triage' : 'Continue'}
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/staff/patients/${patient.id}/chart`);
                            }}
                            className="btn-outline text-sm py-2 px-4"
                          >
                            View Chart
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4 mt-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{queue.filter(q => q.status === 'pending').length}</div>
          <div className="text-sm text-amber-700">Pending</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{queue.filter(q => q.status === 'in_triage').length}</div>
          <div className="text-sm text-blue-700">In Triage</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">{queue.filter(q => q.status === 'ready_for_provider').length}</div>
          <div className="text-sm text-green-700">Ready for Provider</div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-600">{allPatients.length}</div>
          <div className="text-sm text-slate-700">Total Patients</div>
        </div>
      </div>
    </div>
  );
}
